import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import CustomSelect from "./ui/CustomSelect";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const AllCities = () => {
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [modalSelectedCountry, setModalSelectedCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [cityName, setCityName] = useState("");
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [saveLoading, setSaveLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCities = async (
    page = 1,
    perPage = 10,
    countryId = "",
    search = "",
    sortBy = null,
    sortOrder = "asc"
  ) => {
    try {
      setLoading(true);
      setErrorMessage("");

      let url = `/get-all-cities?page=${page}&per_page=${perPage}`;
      if (countryId) url += `&country_id=${encodeURIComponent(countryId)}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (sortBy) url += `&sort_by=${sortBy}`;
      if (sortOrder) url += `&sort_order=${sortOrder}`;

      const response = await apiCall("get", url, null, navigate);
      console.log("fetchCities API Response:", response.data);

      if (response?.data?.success) {
        const cityList = response.data.data || [];
        const pagination = response.data.pagination ||
          response.data || {
            total: cityList.length || 0,
            current_page: page,
            per_page: perPage,
            last_page: Math.ceil((cityList.length || 0) / perPage) || 1,
          };
        setCities(cityList);
        setFilteredCities(cityList);
        setTotalEntries(pagination.total || 0);
        setTotalPages(parseInt(pagination.last_page, 10) || 1);
        setCurrentPage(parseInt(pagination.current_page, 10) || page);
        handleApiResponse(response.data, "success", "GET");
      } else {
        setCities([]);
        setFilteredCities([]);
        setTotalEntries(0);
        setTotalPages(1);
        setCurrentPage(1);
        setErrorMessage(response?.data?.message || "Failed to load cities.");
        handleCustomResponse(
          "error",
          response?.data?.message || "Failed to load cities."
        );
      }
    } catch (error) {
      console.error("fetchCities Error:", error);
      handleApiResponse(error.response?.data, "Err", "GET");
      setErrorMessage(
        error?.response?.data?.message || "Error fetching cities."
      );
      setCities([]);
      setFilteredCities([]);
      setTotalEntries(0);
      setTotalPages(1);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoading(true);
      setCountriesLoading(true);
      const queryParams = new URLSearchParams({
        want_pagination: 0,
        sort_by: "country_name",
        sort_order: "asc",
      });

      const response = await apiCall(
        "get",
        `/get-all-countries?${queryParams.toString()}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        const countriesData = response.data.data || [];
        setCountries(countriesData);
        handleApiResponse(response.data, "success", "GET");
      } else {
        handleCustomResponse("error", "Failed to load countries.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "GET");
    } finally {
      setCountriesLoading(false);
    }
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFetchCities = useCallback(
    debounce((page, perPage, countryId, search) => {
      fetchCities(
        page,
        perPage,
        countryId,
        search,
        sortConfig.field,
        sortConfig.direction
      );
    }, 300),
    [sortConfig]
  );

  const handleSort = (field) => {
    const newDirection =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction: newDirection });
    fetchCities(
      currentPage,
      entriesPerPage,
      selectedCountry,
      searchTerm,
      field,
      newDirection
    );
  };

  useEffect(() => {
    debouncedFetchCities(
      currentPage,
      entriesPerPage,
      selectedCountry,
      searchTerm
    );
  }, [currentPage, entriesPerPage, selectedCountry, searchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    debouncedFetchCities(1, entriesPerPage, selectedCountry, value);
  };

  const handleCountryChange = (selected) => {
    setSelectedCountry(selected?.value || "");
    setCurrentPage(1);
    debouncedFetchCities(1, entriesPerPage, selected?.value || "", searchTerm);
  };

  const handlePerPageChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
    fetchCities(
      1,
      value,
      selectedCountry,
      searchTerm,
      sortConfig.field,
      sortConfig.direction
    );
  };

  const handlePageChange = (page) => {
    console.log(
      "Changing to page:",
      page,
      "Total Pages:",
      totalPages,
      "Current Page:",
      currentPage
    );
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchCities(
        page,
        entriesPerPage,
        selectedCountry,
        searchTerm,
        sortConfig.field,
        sortConfig.direction
      );
    }
  };

  const handleOpenModal = (type, city = null) => {
    setModalType(type);
    setShowModal(true);
    setModalError("");
    setModalSuccess("");
    setSaveLoading(false);
    setCityName("");
    setModalSelectedCountry("");
    setSelectedCity(null);
    if (type === "edit" && city) {
      setCityName(city.city_name || "");
      setModalSelectedCountry(city.country_id || "");
      setSelectedCity(city);
    } else if (type === "delete" && city) {
      setSelectedCity(city);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setCityName("");
    setModalSelectedCountry("");
    setModalError("");
    setModalSuccess("");
    setSelectedCity(null);
    setSaveLoading(false);
  };

  const handleAddCity = async (e) => {
    e.preventDefault();
    if (!cityName.trim()) {
      handleCustomResponse("error", "City name is required.");
      return;
    }
    if (!modalSelectedCountry) {
      handleCustomResponse("error", "Please select a country.");
      return;
    }

    try {
      setModalError("");
      setModalSuccess("");
      setSaveLoading(true);
      const response = await apiCall(
        "post",
        "/create-city",
        {
          country_id: parseInt(modalSelectedCountry),
          city_name: cityName,
        },
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "POST");
        setCityName("");
        setModalSelectedCountry("");
        await fetchCities(
          currentPage,
          entriesPerPage,
          selectedCountry,
          searchTerm,
          sortConfig.field,
          sortConfig.direction
        );
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to add city.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "POST");
      setModalSuccess("");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEditCity = async (e) => {
    e.preventDefault();
    if (!cityName.trim()) {
      handleCustomResponse("error", "City name is required.");
      return;
    }

    try {
      setSaveLoading(true);
      setModalError("");
      setModalSuccess("");

      const response = await apiCall(
        "put",
        `/update-city/${selectedCity.city_id}`,
        { city_name: cityName },
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "PUT");
        await fetchCities(
          currentPage,
          entriesPerPage,
          selectedCountry,
          searchTerm,
          sortConfig.field,
          sortConfig.direction
        );
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to update city.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "PUT");
      setModalSuccess("");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteCity = async () => {
    try {
      setSaveLoading(true);
      setModalError("");
      setModalSuccess("");

      const response = await apiCall(
        "delete",
        `/delete-city/${selectedCity.city_id}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(response.data, "success", "DELETE");
        await fetchCities(
          currentPage,
          entriesPerPage,
          selectedCountry,
          searchTerm,
          sortConfig.field,
          sortConfig.direction
        );
        handleCloseModal();
      } else {
        handleCustomResponse("error", "Failed to delete city.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "DELETE");
      setModalSuccess("");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (modalType === "add") {
      handleAddCity(e);
    } else if (modalType === "edit") {
      handleEditCity(e);
    } else if (modalType === "delete") {
      handleDeleteCity();
    }
  };

  const startIndex =
    totalEntries > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0;
  const endIndex = Math.min(currentPage * entriesPerPage, totalEntries);

  if (errorMessage) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by city name..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={entriesPerPage}
              handlePerPageChange={handlePerPageChange}
            />
            <div style={{ minWidth: "200px" }}>
              <CustomSelect
                name="country"
                value={selectedCountry}
                onChange={handleCountryChange}
                options={countries}
                placeholder="Select Country"
                labelKey="country_name"
                valueKey="country_id"
                isLoading={countriesLoading}
                isClearable={true}
                isSearchable={true}
                noOptionsMessage={() => "No countries found"}
              />
            </div>
            <div className="ms-auto">
              {hasPermission("insert_cities") && (
                <button
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                  onClick={() => handleOpenModal("add")}
                >
                  <Icon icon="ri:add-line" className="me-2" /> Add City
                </button>
              )}
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          <div className="position-relative">
            {filteredCities.length === 0 && !loading ? (
              <TableDataNoAvailable message="No cities found." />
            ) : (
              <div
                                className="table-responsive scroll-sm"

                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        S.No
                      </th>
                      <th
                        scope="col"
                        className="p-2"
                        onClick={() => handleSort("city_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>City Name</span>
                          <SortIcons
                            field="city_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-md-table-cell"
                        onClick={() => handleSort("country_name")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Country Name</span>
                          <SortIcons
                            field="country_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-md-table-cell"
                        onClick={() => handleSort("created_by")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created By</span>
                          <SortIcons
                            field="created_by"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        className="p-2 d-lg-table-cell"
                        onClick={() => handleSort("created_datetime")}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th scope="col" className="p-2">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCities.map((city, index) => (
                      <tr key={city.city_id}>
                        <td className="p-2">{startIndex + index}</td>
                        <td className="p-2">
                          <span className="">
                            {city.city_name || `City #${city.city_id}`}
                          </span>
                        </td>
                        <td className="p-2 d-md-table-cell">
                          {city.country_name || "-"}
                        </td>
                        <td className="p-2 d-md-table-cell">
                          {city.created_by || "-"}
                        </td>
                        <td className="p-2 d-lg-table-cell">
                          {city.created_datetime
                            ? new Date(
                                city.created_datetime
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "-"}
                        </td>
                        <td className="p-2">
                          {hasPermission("update_cities") && (
                            <button
                              className="w-32-px h-32-px me-2 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Edit";
                                tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                tooltip.id = "edit-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("edit-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                              onClick={() => handleOpenModal("edit", city)}
                            >
                              <Icon icon="lucide:edit" />
                            </button>
                          )}
                          {hasPermission("delete_cities") && (
                            <button
                              className="w-32-px h-32-px me-2 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Delete";
                                tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                tooltip.id = "delete-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("delete-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                              onClick={() => handleOpenModal("delete", city)}
                            >
                              <Icon icon="mingcute:delete-2-line" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {loading && (
              <div
                className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                style={{
                  zIndex: 10,
                  borderRadius: "10px",
                  padding: "20px",
                }}
              >
                <LoadingComponent />
              </div>
            )}

            <div className="pagination-container" style={{ marginTop: "20px" }}>
              <PaginationControls
                pagination={{
                  currentPage,
                  perPage: entriesPerPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalType === "add"
                  ? "Add New City"
                  : modalType === "edit"
                  ? "Edit City"
                  : "Delete City"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                disabled={saveLoading}
              ></button>
            </div>
            <div className="modal-body p-24">
              {saveLoading ? (
                <div className="text-center my-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </div>
                  <p className="mt-2">Processing...</p>
                </div>
              ) : modalType === "delete" ? (
                <p>
                  Are you sure you want to delete{" "}
                  <strong>{selectedCity?.city_name || "this city"}</strong>?
                </p>
              ) : (
                <div>
                  <div className="row">
                    {modalType === "add" && (
                      <div className="col-12 mb-20">
                        <label
                          htmlFor="countrySelect"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          Country
                        </label>
                        <CustomSelect
                          name="countrySelect"
                          value={modalSelectedCountry}
                          onChange={(selected) =>
                            setModalSelectedCountry(selected?.value || "")
                          }
                          options={countries}
                          placeholder="Select Country"
                          labelKey="country_name"
                          valueKey="country_id"
                          isSearchable={true}
                          isClearable={true}
                          isLoading={countriesLoading}
                          noOptionsMessage={() => "No countries found"}
                        />
                      </div>
                    )}
                    <div className="col-12 mb-20">
                      <label
                        htmlFor="cityName"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        City Name
                      </label>
                      <input
                        type="text"
                        className="form-control radius-8"
                        id="cityName"
                        value={cityName}
                        onChange={(e) => setCityName(e.target.value)}
                        placeholder="Enter city name"
                        disabled={modalSuccess}
                      />
                    </div>
                    <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                      <button
                        type="button"
                        className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-50 py-11 radius-8"
                        onClick={handleCloseModal}
                        disabled={saveLoading || modalSuccess}
                      >
                        Cancel
                      </button>
                      {!modalSuccess && (
                        <button
                          type="button"
                          className="btn btn-primary border border-primary-600 text-md px-50 py-12 radius-8"
                          onClick={handleSubmit}
                          disabled={saveLoading}
                        >
                          {modalType === "add" ? "Add City" : "Update City"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {modalType === "delete" && (
              <div className="modal-footer p-24 d-flex align-items-center justify-content-center gap-3">
                <button
                  type="button"
                  className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-50 py-11 radius-8"
                  onClick={handleCloseModal}
                  disabled={saveLoading || modalSuccess}
                >
                  Cancel
                </button>
                {!modalSuccess && (
                  <button
                    type="button"
                    className="btn btn-danger border border-primary-600 text-md px-50 py-12 radius-8"
                    onClick={handleSubmit}
                    disabled={saveLoading}
                  >
                    Delete City
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCities;
